import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiErr, apiGet } from "@/lib/api";
import { fmtEUR, fmtDate, initials } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { ContactBadge } from "@/components/StatusBadge";
import { OwnerDocuments } from "@/components/OwnerDocuments";
import { OwnerDialog } from "@/components/dialogs/OwnerDialog";
import { DogDialog } from "@/components/dialogs/DogDialog";
import { PackageDialog } from "@/components/dialogs/PackageDialog";
import { PaymentDialog } from "@/components/dialogs/PaymentDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ArrowLeft, Pencil, Plus, Phone, Mail, MapPin, CreditCard, RotateCcw } from "lucide-react";
import { useOnlineGuard } from "@/pwa/useOnlineGuard";
import { toast } from "sonner";

export default function OwnerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { guard } = useOnlineGuard();
  const [data, setData] = useState(null);
  const [editOwner, setEditOwner] = useState(false);
  const [addDog, setAddDog] = useState(false);
  const [addPkg, setAddPkg] = useState(false);
  const [addPay, setAddPay] = useState(false);
  const [converting, setConverting] = useState(false);
  const [fu, setFu] = useState({ date: "", note: "" });
  const [closeOpen, setCloseOpen] = useState(false);
  const [uc, setUc] = useState({ open: false, target: "", amount: "", note: "", key: "" });

  const applyCredit = async () => {
    const [tt, tid] = (uc.target || "").split(":");
    if (!tt || !tid) return toast.error("Seleziona una destinazione.");
    const amt = Number(uc.amount);
    if (!amt || amt <= 0) return toast.error("Inserisci un importo valido.");
    try {
      await api.post(`/owners/${id}/use-credit`, { target_type: tt, target_id: tid, amount: amt, notes: uc.note, request_key: uc.key });
      toast.success("Credito cliente utilizzato");
      setUc({ open: false, target: "", amount: "", note: "", key: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const load = useCallback(() => {
    apiGet(`/owners/${id}`).then(({ data }) => setData(data)).catch((e) => { if (!e.isOffline) toast.error(apiErr(e)); });
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (data?.owner) setFu({ date: data.owner.follow_up_date || "", note: data.owner.follow_up_note || "" }); }, [data]);

  const setStage = async (stage) => {
    try { await api.patch(`/owners/${id}/contact-stage`, { contact_stage: stage }); toast.success("Stato contatto aggiornato"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const saveFollowUp = async () => {
    try { await api.patch(`/owners/${id}/follow-up`, { follow_up_date: fu.date, follow_up_note: fu.note }); toast.success("Follow-up salvato"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const onOwnerSaved = async () => {
    if (converting) {
      try { await api.patch(`/owners/${id}/contact-stage`, { contact_stage: "Cliente" }); toast.success("Contatto convertito in cliente"); }
      catch (e) { toast.error(apiErr(e)); }
    }
    load();
  };

  const deleteOwner = async () => {
    try { await api.delete(`/owners/${id}`); toast.success("Cliente eliminato"); nav("/clienti"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const setOwnerStato = async (stato) => {
    try { await api.patch(`/owners/${id}/stato`, { stato }); toast.success(stato === "Concluso" ? "Cliente archiviato" : "Cliente riattivato"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!data) return <p className="text-muted-foreground">Caricamento…</p>;
  const o = data.owner;
  const isClient = (o.contact_stage || "Cliente") === "Cliente";
  const plannedSessions = [...(data.recent_sessions || []).filter((s) => s.status === "Pianificata"), ...(data.upcoming || [])]
    .filter((s, i, a) => a.findIndex((x) => x.id === s.id) === i);
  const closeContact = () => { if (plannedSessions.length) setCloseOpen(true); else setStage("Chiuso"); };
  const dogNameById = Object.fromEntries((data.dogs || []).map((d) => [d.id, d.nome]));
  const sessDogName = (s) => (s.dog_ids || []).map((id) => dogNameById[id]).filter(Boolean).join(", ") || "—";
  const creditTargets = [
    ...(data.recent_sessions || []).filter((s) => s.billable && s.fin_outstanding > 0).map((s) => ({ v: `session:${s.id}`, res: s.fin_outstanding, line1: `${sessDogName(s)} · ${s.service_type}`, line2: `${fmtDate(s.date)}${s.start_time ? ` · ${s.start_time}` : ""} · residuo ${fmtEUR(s.fin_outstanding)}` })),
    ...(data.packages || []).filter((p) => p.outstanding > 0).map((p) => ({ v: `package:${p.id}`, res: p.outstanding, line1: `Pacchetto · ${dogNameById[p.dog_id] || "—"}`, line2: `${fmtEUR(p.total_price)} · aperto ${fmtDate(p.opening_date)} · residuo ${fmtEUR(p.outstanding)}` })),
  ];
  const hasHistory = data.dogs.length > 0 || data.packages.length > 0 || data.recent_sessions.length > 0 || data.upcoming.length > 0;
  const activeDogs = data.dogs.filter((d) => d.stato !== "Concluso");
  const hasPlanned = data.upcoming.length > 0 || (data.recent_sessions || []).some((s) => s.status === "Pianificata");
  const canArchive = activeDogs.length === 0 && !hasPlanned;

  return (
    <div className="space-y-5">
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" />Indietro</button>

      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-start gap-4">
          <span className="grid place-items-center h-14 w-14 rounded-2xl bg-accent/15 text-accent font-bold text-lg shrink-0">{initials(`${o.nome} ${o.cognome}`)}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <h1 className="font-head text-2xl font-extrabold break-words min-w-0">{o.nome} {o.cognome}</h1>
              <StatusBadge value={o.stato} />
              <ContactBadge value={o.contact_stage} testid="owner-contact-badge" />
            </div>
            <div className="mt-2 grid sm:grid-cols-2 gap-1 text-sm text-muted-foreground">
              {o.telefono && <span className="flex items-center gap-2"><Phone className="h-4 w-4" />{o.telefono}</span>}
              {o.email && <span className="flex items-center gap-2"><Mail className="h-4 w-4" />{o.email}</span>}
              {(o.indirizzo || o.citta) && <span className="flex items-center gap-2"><MapPin className="h-4 w-4" />{[o.indirizzo, o.citta].filter(Boolean).join(", ")}</span>}
            </div>
            {o.note && <p className="mt-2 text-sm break-words">{o.note}</p>}
          </div>
          <Button variant="outline" size="sm" data-testid="edit-owner-btn" onClick={() => setEditOwner(true)} className="rounded-full"><Pencil className="h-4 w-4" /></Button>
        </div>
        {data.outstanding > 0 && (
          <div className="mt-4 rounded-xl bg-[#FFF8E1] border border-[#FFE082] p-3 text-sm font-semibold text-[#8D6E63] flex items-center justify-between">
            <span>Totale da incassare</span><span className="font-mono">{fmtEUR(data.outstanding)}</span>
          </div>
        )}
        {data.client_credit > 0 && (
          <div className="mt-3 rounded-xl bg-[#E8F5E9] border border-[#A5D6A7] p-3 text-sm" data-testid="owner-client-credit">
            <div className="flex items-center justify-between font-semibold text-[#1E5E2B]"><span>Credito cliente disponibile</span><span className="font-mono">{fmtEUR(data.client_credit)}</span></div>
            <p className="mt-1 text-xs text-[#3B7A47]">Il credito non viene compensato automaticamente con gli importi da incassare.</p>
            <Button size="sm" className="mt-2 rounded-full" data-testid="use-credit-btn" onClick={guard(() => setUc({ open: true, target: "", amount: "", note: "", key: (crypto.randomUUID?.() || String(Date.now())) }))}>Usa credito</Button>
          </div>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button variant="outline" data-testid="owner-add-dog-btn" onClick={guard(() => setAddDog(true))} className="rounded-full"><Plus className="h-4 w-4 mr-1" />Cane</Button>
        <Button variant="outline" data-testid="owner-add-pkg-btn" onClick={guard(() => setAddPkg(true))} className="rounded-full"><Plus className="h-4 w-4 mr-1" />Pacchetto</Button>
        <Button variant="outline" data-testid="owner-add-pay-btn" onClick={guard(() => setAddPay(true))} className="rounded-full"><CreditCard className="h-4 w-4 mr-1" />Pagamento</Button>
      </div>

      {o.contact_stage !== "Cliente" && (
        <section className="rounded-2xl border border-[#CE93D8] bg-[#FAF4FB] p-5" data-testid="owner-contact-zone">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <h2 className="font-head text-base font-bold">Gestione contatto</h2>
            <ContactBadge value={o.contact_stage} />
          </div>
          <p className="text-sm text-muted-foreground mb-3">"Converti in cliente" apre la scheda completa con gli stessi dati e lo stesso storico. Nessun ID, cane, pagamento o evento Calendar viene duplicato.</p>
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="convert-to-client-btn" className="rounded-full" onClick={() => { setConverting(true); setEditOwner(true); }}>Converti in cliente</Button>
            {o.contact_stage !== "Occasionale" && <Button variant="outline" data-testid="mark-occasional-btn" className="rounded-full" onClick={() => setStage("Occasionale")}>Segna come occasionale</Button>}
            {o.contact_stage !== "Chiuso"
              ? <Button variant="outline" data-testid="close-contact-btn" className="rounded-full" onClick={closeContact}>Chiudi contatto</Button>
              : <Button variant="outline" data-testid="reopen-contact-btn" className="rounded-full" onClick={() => setStage("Da ricontattare")}>Riapri contatto</Button>}
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Data follow-up</Label><Input type="date" data-testid="follow-up-date" value={fu.date} onChange={(e) => setFu((p) => ({ ...p, date: e.target.value }))} /></div>
            <div className="space-y-1"><Label>Nota follow-up</Label><Input data-testid="follow-up-note" value={fu.note} onChange={(e) => setFu((p) => ({ ...p, note: e.target.value }))} placeholder="es. richiamare per pacchetto" /></div>
          </div>
          <Button size="sm" className="mt-2 rounded-full" data-testid="follow-up-save" onClick={saveFollowUp}>Salva follow-up</Button>
        </section>
      )}

      <section>
        <h2 className="font-head text-lg font-bold mb-2">Cani ({data.dogs.length})</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {data.dogs.map((d) => (
            <button key={d.id} onClick={() => nav(`/cani/${d.id}`)} data-testid={`owner-dog-${d.id}`}
              className="text-left rounded-2xl border border-border bg-card p-4 hover:border-primary">
              <div className="flex items-center gap-3">
                <span className="grid place-items-center h-10 w-10 rounded-xl bg-primary/10 text-primary font-bold shrink-0">{initials(d.nome)}</span>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold break-words">{d.nome}</div>
                  <div className="text-sm text-muted-foreground break-words">{d.razza || "—"}</div>
                </div>
                <div className="ml-auto shrink-0"><StatusBadge value={d.stato} /></div>
              </div>
            </button>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-head text-lg font-bold mb-2">Pacchetti attivi</h2>
        <div className="space-y-2">
          {data.packages.filter((p) => p.status === "Attivo" || p.status === "In esaurimento").map((p) => (
            <div key={p.id} className="rounded-2xl border border-border bg-card p-4 flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="font-semibold">{fmtEUR(p.total_price)} · {p.used_credits}/{p.total_credits} usate</div>
                <div className="text-sm text-muted-foreground">Aperto {fmtDate(p.opening_date)}</div>
              </div>
              <div className="flex gap-2"><StatusBadge value={p.status} /><StatusBadge value={p.payment_status} /></div>
            </div>
          ))}
          {data.packages.filter((p) => p.status === "Attivo" || p.status === "In esaurimento").length === 0 && <p className="text-sm text-muted-foreground">Nessun pacchetto attivo.</p>}
        </div>
      </section>

      <section>
        <h2 className="font-head text-lg font-bold mb-2">Prossime attività</h2>
        <div className="space-y-2">
          {data.upcoming.map((s) => (
            <div key={s.id} className="rounded-xl border border-border bg-card p-3 text-sm flex justify-between">
              <span>{s.service_type} · {fmtDate(s.date)} {s.start_time}</span><StatusBadge value={s.status} />
            </div>
          ))}
          {data.upcoming.length === 0 && <p className="text-sm text-muted-foreground">Nessun appuntamento in programma.</p>}
        </div>
      </section>

      <section>
        <h2 className="font-head text-lg font-bold mb-2">Prestazioni recenti</h2>
        <div className="space-y-2">
          {data.recent_sessions.map((s) => (
            <div key={s.id} className="rounded-xl border border-border bg-card p-3 text-sm">
              <div className="flex justify-between"><span>{s.service_type} · {fmtDate(s.date)}</span><StatusBadge value={s.status} /></div>
              {s.billable && (
                <div className="mt-1 flex gap-3 flex-wrap text-xs text-muted-foreground" data-testid={`sess-fin-${s.id}`}>
                  <span>Valore {fmtEUR(s.value)}</span>
                  <span>Incassato {fmtEUR(s.paid)}</span>
                  {s.client_credit_applied > 0 && <span className="text-[#1E5E2B]">Credito cliente {fmtEUR(s.client_credit_applied)}</span>}
                  {s.fin_outstanding > 0
                    ? <span className="text-[#8D6E63] font-semibold">Da incassare {fmtEUR(s.fin_outstanding)}</span>
                    : <span className="text-[#1E5E2B] font-semibold">Pagato</span>}
                </div>
              )}
            </div>
          ))}
          {data.recent_sessions.length === 0 && <p className="text-sm text-muted-foreground">Nessuna prestazione.</p>}
        </div>
      </section>

      <OwnerDocuments ownerId={o.id} documents={data.documents || []} dogs={data.dogs} onChanged={load} />

      <section className="rounded-2xl border border-border bg-card p-5" data-testid="owner-stato-zone">
        <h2 className="font-head text-base font-bold mb-2">Stato {isClient ? "cliente" : "contatto"}</h2>
        {o.stato === "Concluso" ? (
          <>
            <p className="text-sm text-muted-foreground mb-3">Il cliente è archiviato (Concluso). Puoi riattivarlo senza modificare cani o storico.</p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" data-testid="reactivate-owner-btn" className="rounded-full"><RotateCcw className="h-4 w-4 mr-1" />Riattiva cliente</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Riattivare questo cliente?</AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div>
                      <span className="block">Cliente: <b>{o.nome} {o.cognome}</b></span>
                      <span className="block mt-2">Lo stato tornerà <b>Attivo</b>. Nessun altro dato verrà modificato.</span>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Indietro</AlertDialogCancel>
                  <AlertDialogAction data-testid="reactivate-owner-confirm" onClick={() => setOwnerStato("Attivo")}>Riattiva cliente</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        ) : !canArchive ? (
          <>
            {activeDogs.length > 0 ? (
              <div data-testid="owner-archive-blocked-dogs">
                <p className="text-sm text-muted-foreground mb-2">Non puoi archiviare questo cliente perché ha ancora percorsi attivi.</p>
                <div className="flex flex-wrap gap-2">
                  {activeDogs.map((d) => (
                    <button key={d.id} data-testid={`owner-active-dog-${d.id}`} onClick={() => nav(`/cani/${d.id}`)}
                      className="max-w-full text-left break-words [overflow-wrap:anywhere] text-sm rounded-full border border-border bg-card px-3 py-1 hover:border-primary">{d.nome} · Apri profilo</button>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="owner-archive-blocked-planned">Non puoi archiviare questo cliente perché esistono ancora prestazioni pianificate.</p>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-3">Archivia il cliente mantenendo cani, pacchetti, pagamenti e storico.</p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" data-testid="archive-owner-btn" className="rounded-full">Archivia cliente</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Archiviare questo cliente?</AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div>
                      <span className="block">Cliente: <b>{o.nome} {o.cognome}</b></span>
                      <span className="block mt-2">Storico, cani, pacchetti, pagamenti e crediti resteranno <b>invariati</b>. Lo stato del cliente verrà impostato su <b>Concluso</b>.</span>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Indietro</AlertDialogCancel>
                  <AlertDialogAction data-testid="archive-owner-confirm" onClick={() => setOwnerStato("Concluso")}>Archivia cliente</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
      </section>

      <section className="rounded-2xl border border-destructive/30 bg-destructive/5 p-5" data-testid="owner-danger-zone">
        <h2 className="font-head text-base font-bold text-destructive mb-2">Elimina {isClient ? "cliente" : "contatto"}</h2>
        {hasHistory ? (
          <p className="text-sm text-muted-foreground" data-testid="owner-delete-blocked">Questo cliente ha cani o dati storici (pacchetti, pagamenti, prestazioni) e non può essere eliminato definitivamente. Per archiviarlo, imposta lo stato su <b>Concluso</b> dalla modifica del profilo.</p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-3">Nessun cane o dato storico collegato. Puoi eliminare definitivamente questo cliente.</p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" data-testid="delete-owner-btn" className="rounded-full">Elimina {isClient ? "cliente" : "contatto"}</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Eliminare definitivamente questo cliente?</AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div>
                      <span className="block">Cliente: <b>{o.nome} {o.cognome}</b></span>
                      <span className="block mt-2">L'operazione è irreversibile.</span>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Indietro</AlertDialogCancel>
                  <AlertDialogAction data-testid="delete-owner-confirm" className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={deleteOwner}>Elimina definitivamente</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
      </section>

      <OwnerDialog open={editOwner} onOpenChange={(v) => { setEditOwner(v); if (!v) setConverting(false); }} owner={o} onSaved={onOwnerSaved} />

      <AlertDialog open={uc.open} onOpenChange={(v) => !v && setUc((u) => ({ ...u, open: false }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Usa credito cliente</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-foreground">
                <div className="text-sm">Credito disponibile: <b>{fmtEUR(data.client_credit)}</b></div>
                <div>
                  <label className="text-xs font-medium">Destinazione</label>
                  <Select value={uc.target}
                    onValueChange={(t) => { const tgt = creditTargets.find((x) => x.v === t); const def = tgt ? Math.min(data.client_credit, tgt.res) : ""; setUc((u) => ({ ...u, target: t, amount: def ? String(def) : "" })); }}>
                    <SelectTrigger data-testid="uc-target" className="mt-1 w-full h-auto min-h-9 py-2 items-start text-left whitespace-normal [&>span]:line-clamp-2">
                      <SelectValue placeholder="Seleziona…" />
                    </SelectTrigger>
                    <SelectContent className="max-w-[calc(100vw-2rem)]">
                      {creditTargets.map((t) => (
                        <SelectItem key={t.v} value={t.v} className="whitespace-normal">
                          <span className="block font-medium">{t.line1}</span>
                          <span className="block text-xs text-muted-foreground">{t.line2}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium">Importo da utilizzare (€)</label>
                  <input data-testid="uc-amount" type="number" min="0" value={uc.amount}
                    onKeyDown={(e) => { if (e.key === "-" || e.key === "e") e.preventDefault(); }}
                    onChange={(e) => setUc((u) => ({ ...u, amount: e.target.value }))}
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-medium">Nota (opzionale)</label>
                  <input data-testid="uc-note" value={uc.note} onChange={(e) => setUc((u) => ({ ...u, note: e.target.value }))}
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
                </div>
                {creditTargets.length === 0 && <p className="text-xs text-muted-foreground">Nessun elemento con residuo da coprire per questo proprietario.</p>}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="uc-confirm" onClick={applyCredit}>Applica credito</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={closeOpen} onOpenChange={setCloseOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Chiudere questo contatto?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <span className="block">Questo contatto ha ancora <b>{plannedSessions.length}</b> {plannedSessions.length === 1 ? "appuntamento pianificato" : "appuntamenti pianificati"}.</span>
                <div className="mt-2 space-y-1 text-sm">
                  {plannedSessions.slice(0, 5).map((s) => (
                    <span key={s.id} className="block">· {s.service_type} — {fmtDate(s.date)}{s.start_time ? ` · ${s.start_time}` : ""}</span>
                  ))}
                </div>
                <span className="block mt-2">Gli appuntamenti <b>non</b> verranno annullati o eliminati: verrà modificato solo lo stato del contatto.</span>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="close-contact-anyway" onClick={() => { setCloseOpen(false); setStage("Chiuso"); }}>Chiudi comunque</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <DogDialog open={addDog} onOpenChange={setAddDog} fixedOwnerId={o.id} onSaved={load} />
      <PackageDialog open={addPkg} onOpenChange={setAddPkg} presetOwnerId={o.id} onSaved={load} />
      <PaymentDialog open={addPay} onOpenChange={setAddPay} presetOwnerId={o.id} onSaved={load} />
    </div>
  );
}
